<template>
  <div class="p-4 space-y-4">
    <!-- Encabezado -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-xl font-bold text-gray-900 dark:text-gray-100">{{ titulo }}</h1>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ subtitulo }}</p>
      </div>
      <span class="px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
        En desarrollo
      </span>
    </div>

    <!-- Información de la pantalla -->
    <div class="bg-gradient-to-br from-violet-50 to-blue-50 dark:from-violet-950/30 dark:to-blue-950/30 rounded-lg p-6 border border-violet-200 dark:border-violet-800">
      <div class="flex items-start gap-4">
        <div class="w-12 h-12 rounded-lg bg-violet-600 flex items-center justify-center flex-shrink-0">
          <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div class="flex-1">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">¿Qué se hará en esta pantalla?</h2>
          <p class="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
            {{ descripcion }}
          </p>
        </div>
      </div>
    </div>

    <!-- Funcionalidades principales -->
    <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
        <svg class="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
        Funcionalidades Principales
      </h3>
      <ul class="space-y-2">
        <li
          v-for="(funcionalidad, index) in funcionalidades"
          :key="index"
          class="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300"
        >
          <svg class="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
          </svg>
          <span>{{ funcionalidad }}</span>
        </li>
      </ul>
    </div>

    <!-- Datos que se manejarán -->
    <div class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
        <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
        Datos que se Manejarán
      </h3>
      <ul class="space-y-2">
        <li
          v-for="(dato, index) in datosManejados"
          :key="index"
          class="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300"
        >
          <svg class="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span>{{ dato }}</span>
        </li>
      </ul>
    </div>

    <!-- Mockup visual (opcional) -->
    <div v-if="mostrarMockup" class="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-4">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Vista Previa del Diseño</h3>
      <div class="bg-gray-100 dark:bg-gray-800 rounded-lg p-8 border-2 border-dashed border-gray-300 dark:border-gray-700">
        <div class="text-center text-gray-400">
          <svg class="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p class="text-sm">Mockup en desarrollo</p>
        </div>
      </div>
    </div>

    <!-- Estado de desarrollo -->
    <div class="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
      <div class="flex items-start gap-3">
        <svg class="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <h4 class="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">Información de Desarrollo</h4>
          <p class="text-xs text-blue-800 dark:text-blue-200">
            Esta pantalla está en desarrollo y será implementada próximamente con todas las funcionalidades descritas.
            El backend ya está configurado y listo para conectarse.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
defineProps({
  titulo: {
    type: String,
    required: true
  },
  subtitulo: {
    type: String,
    required: true
  },
  descripcion: {
    type: String,
    required: true
  },
  funcionalidades: {
    type: Array,
    default: () => []
  },
  datosManejados: {
    type: Array,
    default: () => []
  },
  mostrarMockup: {
    type: Boolean,
    default: true
  }
})
</script>
